import { singleton } from 'tsyringe';
import type { CrosspointDataProvider } from '@o2d/datasource';

/**
 * Singleton service that holds references to timeline DataProviders
 * and invalidates cached items when issues change.
 *
 * Providers are registered via `data-provider-ready` events from o2d-timeline.
 * Multiple views can register independent invalidation callbacks keyed by provider key.
 */
@singleton()
export class TimelineInvalidationService {

    private readonly _providers = new Map<string, CrosspointDataProvider<unknown, unknown, unknown>>();
    private readonly _onInvalidatedCallbacks = new Map<string, () => void>();

    /**
     * Register a data provider by its composite key (e.g. 'B:::D').
     *
     * @param key - The provider's composite key from buildDataProviderId()
     * @param provider - The CrosspointDataProvider instance
     */
    registerProvider(key: string, provider: CrosspointDataProvider<unknown, unknown, unknown>): void {
        this._providers.set(key, provider);
    }

    /**
     * Unregister a data provider by its composite key.
     *
     * @param key - The provider's composite key to remove
     */
    unregisterProvider(key: string): void {
        this._providers.delete(key);
    }

    /**
     * Register an invalidation callback for a specific provider key.
     * Typically wired to `triggerAreaReRender()` in the timeline view.
     *
     * @param key - The provider's composite key
     * @param callback - Function to call after items are invalidated
     */
    setOnInvalidatedCallback(key: string, callback: () => void): void {
        this._onInvalidatedCallbacks.set(key, callback);
    }

    /**
     * Remove the invalidation callback for a specific provider key.
     * Should be called in disconnectedCallback to prevent stale references.
     *
     * @param key - The provider's composite key to remove callback for
     */
    removeOnInvalidatedCallback(key: string): void {
        this._onInvalidatedCallbacks.delete(key);
    }

    /**
     * Get a registered provider by its composite key.
     *
     * @param key - The provider's composite key
     * @returns The provider instance, or undefined if not registered
     */
    getProvider(key: string): CrosspointDataProvider<unknown, unknown, unknown> | undefined {
        return this._providers.get(key);
    }

    /**
     * Get all registered provider entries as an array.
     * Returns [key, provider] tuples for iteration in EntityChangeListener.
     *
     * @returns Array of [key, provider] tuples
     */
    getProviderEntries(): [string, CrosspointDataProvider<unknown, unknown, unknown>][] {
        return [...this._providers.entries()];
    }

    /**
     * Invalidate all cached entries for the given issue UID across all registered providers.
     * Does NOT trigger re-rendering — call notifyInvalidated() after all invalidations are done.
     *
     * @param uid - The issue UID to invalidate (used as prefix for composite IDs)
     */
    invalidateIssue(uid: string): void {
        for (const provider of this._providers.values()) {
            provider.invalidateItem(uid);
        }
    }

    /**
     * Invalidate the entire cache for every registered provider whose key passes the filter.
     *
     * Why a key filter is REQUIRED: timeline views register multiple providers (content, header,
     * y-axis). A blanket clear would race with PRD-007 `data-provider-ready` and wipe
     * unrelated axis caches. Callers MUST scope to their content provider key, e.g.
     * `keyFilter: key => key === this.contentProviderKey`.
     *
     * No default is provided to force every caller to make the scoping decision explicitly —
     * a forgotten filter argument used to default to "match all", which silently violated
     * the PRD-007 invariant. If a global wipe is genuinely intended, pass `() => true`.
     *
     * No-op when the provider map is empty (safe during view init before
     * `data-provider-ready` has fired). Does NOT trigger re-rendering — caller must invoke
     * notifyInvalidated() afterwards.
     *
     * @param keyFilter - Predicate selecting which providers to clear (REQUIRED, no default)
     */
    invalidateAllProviders(keyFilter: (key: string) => boolean): void {
        for (const [key, provider] of this._providers.entries()) {
            if (keyFilter(key)) {
                provider.clearCache();
            }
        }
    }

    /**
     * Notify all registered callbacks that invalidation is complete.
     * Triggers re-rendering in all timeline views via requestDataRefresh().
     * Call this ONCE after all invalidations (old position + new position) are done.
     */
    notifyInvalidated(): void {
        for (const callback of this._onInvalidatedCallbacks.values()) {
            callback();
        }
    }
}
